import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AppContext } from "../context/AppContext";
import Cookies from "js-cookie";

import NavbarMain from "./NavbarMain";
import NavbarLogin from "./NavbarLogin";
import NavBarApp from "./NavbarApp";
import NavbarNotFound from "./NavbarNotFound";
import NavbarTools from "./NavbarTools";
import ButtonBtn from "./buttons/ButtonBtn";
import sendEvent from "../utils/functions/sendEvent";

const Header = () => {
  const location = useLocation();
  const { pathname } = location;
  const { lng } = useParams();
  const navigate = useNavigate();

  const [isNotFound, setIsNotFound] = useState(false);
  const headerRef = useRef(null);

  const {
    setAppContentType,
    setActiveItem,
    navbarType,
    setNavbarType,
    setIsUserDataLoading,
  } = useContext(AppContext);

  // Nastavení header v případě, že je stránka 404
  useEffect(() => {
    if (Cookies.get("notfound")) {
      setIsNotFound(true);
    }
  }, []);

  const isLogin = pathname.includes("login");
  const isTwoFactor = pathname.includes("two-factor");
  const isVerifyOtp = pathname.includes("verify-otp");
  const isMainPage = !!pathname.split("/")[2];
  const isRegistrationAccount = pathname.includes("registration-account");
  const isShopOption = pathname.includes("shop-option");
  const isUsers = pathname.includes("users");
  const isTermsOfService = pathname.includes("terms-of-service");
  const isPrivacyPolicy = pathname.includes("privacy-policy");
  const isGoogleTermsPage = pathname.includes("google-terms");
  const isPasswordRestore = pathname.includes("user-password-reset-confirm");
  const isPageNotFound = pathname.includes("404");
  const isTest = pathname.includes("test");

  // Registrace
  const isRegisterShop = pathname.includes("register-shop");
  const isCompanyDetails = pathname.includes("company-details");
  const isUploadProducts = pathname.includes("upload-products");
  const isConnectGoogle = pathname.includes("connect-google");
  const isInstallTrackingCode = pathname.includes("install-tracking-code");

  // Aplikace
  const isDashboard = pathname.includes("dashboard");
  const isStatistics = pathname.includes("statistics");
  const isCampaigns = pathname.includes("campaigns");
  const isCredit = pathname.includes("credit");
  const isSettings = pathname.includes("settings");
  const isHelp = pathname.includes("help");
  const isContactUs = pathname.includes("contact-us");
  const isInvoice = pathname.includes("invoice");
  const isProducts = pathname.includes("products");
  // App hub (root of app segment)
  const isAppHub = /\/app\/?$/.test(pathname);

  // Doplňky
  const isHeatmaps = pathname.includes("heatmaps");
  const isTools = pathname.includes("/tools/");

  // Blog
  const isBlog = pathname.includes("blog");

  // Nastavení navbarType na základě cesty
  useEffect(() => {
    if (
      isLogin ||
      isTwoFactor ||
      isVerifyOtp ||
      isAppHub ||
      isShopOption ||
      isUsers ||
      isRegistrationAccount ||
      isRegisterShop ||
      isCompanyDetails ||
      isUploadProducts ||
      isConnectGoogle ||
      isInstallTrackingCode ||
      isTermsOfService ||
      isPrivacyPolicy ||
      isGoogleTermsPage ||
      isBlog ||
      isPasswordRestore ||
      isTest
    ) {
      setNavbarType("login");
      sessionStorage.setItem("nt", "login");
    } else if (isHeatmaps || isTools) {
      setNavbarType("tools");
    } else if (
      isDashboard ||
      isStatistics ||
      isCampaigns ||
      isCredit ||
      isSettings ||
      isHelp ||
      isContactUs ||
      isInvoice ||
      isProducts
    ) {
      setNavbarType("app");
      sessionStorage.setItem("nt", "app");
    } else if (!isMainPage) {
      setNavbarType("main");
      sessionStorage.setItem("nt", "main");
    } else {
      setNavbarType("404");
      sessionStorage.setItem("nt", "404");
    }
  }, [
    isLogin,
    isTwoFactor,
    isVerifyOtp,
    isAppHub,
    isShopOption,
    isUsers,
    isRegistrationAccount,
    isRegisterShop,
    isCompanyDetails,
    isUploadProducts,
    isConnectGoogle,
    isInstallTrackingCode,
    isTermsOfService,
    isPrivacyPolicy,
    isGoogleTermsPage,
    isBlog,
    isPasswordRestore,
    isDashboard,
    isStatistics,
    isCampaigns,
    isCredit,
    isSettings,
    isHelp,
    isContactUs,
    isInvoice,
    isProducts,
    isMainPage,
    isTest,
    isHeatmaps,
    isTools,
  ]);

  // Mapování cesty na aktivní položku a appContentType
  useEffect(() => {
    const stateMapping = {
      dashboard: isDashboard,
      statistics: isStatistics,
      campaigns: isCampaigns,
      credit: isCredit,
      settings: isSettings,
      help: isHelp,
      "contact-us": isContactUs,
      invoice: isInvoice,
      products: isProducts,
    };

    for (const [key, value] of Object.entries(stateMapping)) {
      if (value) {
        setActiveItem(key);
        setAppContentType(key);
        break;
      }
    }
  }, [
    isDashboard,
    isStatistics,
    isCampaigns,
    isCredit,
    isSettings,
    isHelp,
    isContactUs,
    isInvoice,
    isProducts,
    setActiveItem,
    setAppContentType,
  ]);

  // Skutečná výška headeru do --header-h, aby se obsah nemusel odsazovat
  // hardcodovanými hodnotami pro každý breakpoint
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    let frame = null;
    let lastHeight = null;

    const measureHeight = () => {
      let height = el.getBoundingClientRect().height;
      // NavbarTools i NavbarNotFound si do tohoto obalu renderují vlastní
      // position: fixed hlavičku, obal pak zkolabuje – bereme tu vyšší z obou.
      for (const child of el.children) {
        height = Math.max(height, child.getBoundingClientRect().height);
      }
      return Math.round(height);
    };

    const applyHeaderHeight = () => {
      frame = null;
      const height = measureHeight();
      // Zápis jen při skutečné změně – jinak by se observer budil dokola
      if (height === lastHeight) return;
      lastHeight = height;
      document.documentElement.style.setProperty("--header-h", `${height}px`);
    };

    // Zápis až v dalším frame, aby neběžel uvnitř doručování ResizeObserveru
    // (jinak Chrome hlásí "ResizeObserver loop completed with undelivered notifications")
    const scheduleHeaderHeight = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(applyHeaderHeight);
    };

    applyHeaderHeight();

    const observer = new ResizeObserver(scheduleHeaderHeight);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    window.addEventListener("orientationchange", scheduleHeaderHeight);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("orientationchange", scheduleHeaderHeight);
    };
  }, [navbarType]);

  return (
    <div
      ref={headerRef}
      className={`header ${navbarType === "main" ? "header--with-banner" : ""}`}
    >
      {navbarType === "main" && (
        <button
          type="button"
          className="header__banner"
          onClick={() => {
            sendEvent("heatmap_banner_click");
            navigate(`/${lng || "cs"}/tools/heatmaps/`);
          }}
        >
          <span className="header__banner-text">
            🔥 <strong>Novinka:</strong> PermoniQ Heatmapy – AI analýza bannerů
            za pár vteřin
          </span>
          <span className="header__banner-text header__banner-text--short">
            🔥 <strong>Novinka:</strong> AI Heatmapy
          </span>
          <span className="header__banner-cta">Otestovat zdarma</span>
        </button>
      )}
      {navbarType === "login" && <NavbarLogin />}
      {navbarType === "tools" && <NavbarTools />}
      {navbarType === "app" && <NavBarApp />}
      {navbarType === "main" && <NavbarMain />}
      {navbarType === "404" && <NavbarNotFound />}
    </div>
  );
};

export default Header;
